import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '../lib/cn'

export interface Aba {
  /** Identificador estável. Não é rótulo: trocar o texto da aba não pode trocar a chave. */
  id: string
  rotulo: string
  /** Número ao lado do rótulo, quando ele ajuda a decidir se vale abrir (pendências, itens). */
  contador?: number
}

interface Props {
  abas: Aba[]
  ativa: string
  aoMudar: (id: string) => void
  /** Nome do conjunto para quem usa leitor de tela, ex.: "Seções da solicitação". */
  rotulo: string
  children: ReactNode
}

/**
 * Para onde cada tecla leva. Setas circulam, Home e End vão às pontas — é o que a
 * especificação WAI-ARIA define para abas, e é o que quem usa leitor de tela espera
 * encontrar. Sem isso, Tab passa por cada aba uma a uma e o teclado fica mais lento que
 * o mouse justamente para quem não tem mouse.
 */
const MOVIMENTOS: Record<string, (indice: number, total: number) => number> = {
  ArrowRight: (indice, total) => (indice + 1) % total,
  ArrowLeft: (indice, total) => (indice - 1 + total) % total,
  Home: () => 0,
  End: (_indice, total) => total - 1,
}

/**
 * Abas acessíveis, controladas por quem as usa.
 *
 * Controladas de fora porque a aba aberta é informação da tela, não do componente: a de
 * detalhe da solicitação precisa poder abrir direto no histórico quando o link vem de um
 * e-mail. Guardar isso aqui dentro tornaria esse caso impossível sem gambiarra.
 *
 * O foco anda junto com a seleção (`tabIndex` só na aba ativa): assim Tab entra no conjunto
 * de abas e sai dele numa tecla, em vez de parar em cada uma.
 *
 * @param props as abas, qual está ativa, o que fazer ao trocar e o conteúdo do painel
 * @returns as abas e o painel da aba ativa
 */
export function Abas({ abas, ativa, aoMudar, rotulo, children }: Props) {
  const base = useId()
  const botoes = useRef<Record<string, HTMLButtonElement | null>>({})

  const idDaAba = (id: string) => `${base}-aba-${id}`
  const idDoPainel = (id: string) => `${base}-painel-${id}`

  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>) {
    const mover = MOVIMENTOS[evento.key]
    if (!mover || abas.length === 0) {
      return
    }
    evento.preventDefault()
    const atual = Math.max(
      abas.findIndex((aba) => aba.id === ativa),
      0,
    )
    const destino = abas[mover(atual, abas.length)]
    if (destino) {
      aoMudar(destino.id)
      botoes.current[destino.id]?.focus()
    }
  }

  return (
    <div>
      {/* A linha de base é uma sombra interna, e não `border-b` com a aba em `-mb-px`: com
          `overflow-x-auto`, o eixo vertical vira `auto` também, e aquele 1px que a aba avançava
          sobre a borda dava à faixa uma rolagem VERTICAL — as setinhas que apareciam ao lado das
          abas da atividade nos repositórios. A sombra fica por baixo da aba, que a cobre sem sair
          da caixa. */}
      <div
        role="tablist"
        aria-label={rotulo}
        onKeyDown={aoTeclar}
        className="flex overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_0_theme(colors.slate.200)]"
      >
        {abas.map((aba) => {
          const selecionada = aba.id === ativa
          return (
            <button
              key={aba.id}
              ref={(no) => {
                botoes.current[aba.id] = no
              }}
              id={idDaAba(aba.id)}
              type="button"
              role="tab"
              aria-selected={selecionada}
              aria-controls={idDoPainel(aba.id)}
              tabIndex={selecionada ? 0 : -1}
              onClick={() => aoMudar(aba.id)}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium',
                'transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
                'focus-visible:outline-niteroi-600',
                selecionada
                  ? 'border-niteroi-700 text-niteroi-800'
                  : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900',
              )}
            >
              {aba.rotulo}
              {aba.contador !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                    selecionada ? 'bg-niteroi-50 text-niteroi-800' : 'bg-slate-100 text-slate-600',
                  )}
                >
                  {aba.contador}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* `tabIndex` no painel para que o conteúdo continue alcançável pelo teclado mesmo
          quando não há nada focável dentro dele — um painel só de texto, por exemplo. */}
      <div
        role="tabpanel"
        id={idDoPainel(ativa)}
        aria-labelledby={idDaAba(ativa)}
        tabIndex={0}
        className="pt-5"
      >
        {children}
      </div>
    </div>
  )
}
